const API_URL = import.meta.env.VITE_API_URL || ''

export const INSTITUTIONAL_EMAIL = 'catalisa@portovelho.ro.gov.br'

export interface Stage {
  id: string
  stage: number
  title: string
  description: string
  status: string
  duration: string
}

export interface DocumentData {
  id: string
  title: string
  description: string | null
  filename: string
  originalName: string
  fileSize: number
  mimeType: string
  category: string
  isPublished: boolean
  order: number
  createdAt: string
}

export interface UploadDocumentData {
  title: string
  description?: string
  category?: string
  order?: number
  file: File
}

export interface UpdateDocumentData {
  title?: string
  description?: string
  category?: string
  order?: number
  isPublished?: boolean
}

export interface ConsultaSubmissionData {
  tipo: string
  nome?: string
  email?: string
  organizacao?: string
  perfil?: string
  desafio?: string
  contribuicao: string
  justificativa?: string
  consentimento: boolean
  cpfCnpj?: string
  telefone?: string
  trl?: string
  trlDescricao?: string
  riscosRequisitos?: string
  custos?: string
  melhoriaDesafio?: string
  comentariosExtras?: string
}

export interface ConsultaSubmission extends ConsultaSubmissionData {
  id: string
  createdAt: string
  ipAddress: string | null
}

export interface HeroContent {
  badge?: string
  title?: string
  subtitle?: string
  description?: string
}

export interface ChallengeContent {
  id: string
  title: string
  question: string
  result: string
}

export interface ScheduleItem {
  date: string
  stage: string
  note: string
}

export interface AvisoContent {
  enabled: boolean
  title: string
  message: string
  publishNote: string
}

export interface InscricoesContent {
  aberta: boolean
  mensagem: string
}

export interface SiteContentMap {
  hero?: HeroContent
  challenges?: ChallengeContent[]
  schedule?: ScheduleItem[]
  legalBases?: string[]
  aviso?: AvisoContent
  inscricoes?: InscricoesContent
}

// ==================== TOKEN ====================

export function triggerLogoutEvent(): void {
  window.dispatchEvent(new CustomEvent('auth:logout'))
  console.log('[TOKEN] Evento de logout disparado')
}

export function isTokenValid(token: string): boolean {
  if (!token || token.trim() === '') return false
  
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return false
    
    const payload = JSON.parse(atob(parts[1]))
    const expDate = new Date(payload.exp * 1000)
    const now = new Date()
    const marginMs = 5 * 60 * 1000
    const isValid = expDate > new Date(now.getTime() + marginMs)
    
    if (!isValid) {
      console.warn('[TOKEN] Token expirado em:', expDate.toLocaleString())
    }
    return isValid
  } catch (error) {
    console.error('[TOKEN] Erro ao validar token:', error)
    return false
  }
}

export function getTokenFromStorage(): string | null {
  return localStorage.getItem('catalisa_token')
}

export function saveTokenToStorage(token: string): void {
  localStorage.setItem('catalisa_token', token)
}

export function clearTokenFromStorage(): void {
  localStorage.removeItem('catalisa_token')
  localStorage.removeItem('catalisa_admin_data')
  console.log('[TOKEN] Token removido do storage')
}

async function authFetch<T>(url: string, token: string, options: RequestInit = {}): Promise<T> {
  if (!token) {
    clearTokenFromStorage()
    triggerLogoutEvent()
    throw new Error('Token não encontrado. Faça login novamente.')
  }
  
  if (!isTokenValid(token)) {
    clearTokenFromStorage()
    triggerLogoutEvent()
    throw new Error('Token expirado. Faça login novamente.')
  }
  
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
        'Authorization': `Bearer ${token}`
      }
    })
    
    if (response.status === 401 || response.status === 403) {
      clearTokenFromStorage()
      triggerLogoutEvent()
      throw new Error('Sessão expirada. Faça login novamente.')
    }
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }))
      throw new Error(errorData.error || `Erro HTTP ${response.status}`)
    }
    
    return response.json()
  } catch (error) {
    if (error instanceof Error) {
      if (error.message.includes('Token') || error.message.includes('Sessão')) {
        clearTokenFromStorage()
        triggerLogoutEvent()
      }
      throw error
    }
    throw new Error('Erro na requisição')
  }
}

// ==================== API ====================

export const api = {
  // ========== STAGES ==========
  
  async getAllStages(): Promise<Stage[]> {
    try {
      const response = await fetch(`${API_URL}/api/stages`)
      if (!response.ok) throw new Error('Erro ao buscar fases')
      return await response.json()
    } catch (error) {
      console.error('[STAGES] Erro:', error)
      return [
        { 
          id: '1', 
          stage: 1, 
          title: 'Alinhamento Institucional', 
          description: 'Definição de desafios prioritários e alinhamento entre Prefeitura e Sebrae.', 
          status: 'concluido', 
          duration: '2 meses' 
        },
        { 
          id: '2', 
          stage: 2, 
          title: 'Consulta Pública ao Mercado', 
          description: 'Coleta de contribuições técnicas da sociedade para qualificar os desafios.', 
          status: 'em-andamento', 
          duration: '30 dias' 
        },
        { 
          id: '3', 
          stage: 3, 
          title: 'Elaboração dos Editais CPSI', 
          description: 'Desenvolvimento dos editais com base nas contribuições recebidas.', 
          status: 'a-iniciar', 
          duration: '2 meses' 
        },
        { 
          id: '4', 
          stage: 4, 
          title: 'Seleção de Soluções', 
          description: 'Avaliação e seleção das propostas inovadoras recebidas.', 
          status: 'a-iniciar', 
          duration: '3 meses' 
        },
        { 
          id: '5', 
          stage: 5, 
          title: 'Contratação e Implementação', 
          description: 'Formalização dos CPSI e acompanhamento da implementação.', 
          status: 'a-iniciar', 
          duration: '12 meses' 
        }
      ]
    }
  },

  async getCurrentStage(): Promise<{ currentStage: number }> {
    try {
      const response = await fetch(`${API_URL}/api/stages/current`)
      if (!response.ok) throw new Error('Erro ao buscar fase atual')
      return await response.json()
    } catch (error) {
      console.error('[CURRENT_STAGE] Erro:', error)
      return { currentStage: 2 }
    }
  },

  async updateStage(
    token: string, 
    stageId: string, 
    data: { status?: string; title?: string; description?: string; duration?: string }
  ): Promise<Stage> {
    return authFetch(`${API_URL}/api/stages/${stageId}`, token, {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  },

  async createStage(
    token: string,
    data: { stage: number; title: string; description: string; status?: string; duration: string }
  ): Promise<Stage> {
    return authFetch(`${API_URL}/api/stages`, token, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  },

  async deleteStage(token: string, stageId: string): Promise<{ success: boolean; message: string }> {
    return authFetch(`${API_URL}/api/stages/${stageId}`, token, {
      method: 'DELETE'
    })
  },

  // ========== ADMIN AUTH ==========
  
  async login(email: string, password: string): Promise<{ token: string; admin: { id: string; email: string; name: string } }> {
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    })
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro no login' }))
      throw new Error(error.error || 'Credenciais inválidas')
    }
    
    const data = await response.json()
    saveTokenToStorage(data.token)
    return data
  },

  async logout(token: string): Promise<void> {
    try {
      if (token && isTokenValid(token)) {
        await fetch(`${API_URL}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        })
      }
    } catch (error) {
      console.error('[LOGOUT] Erro:', error)
    } finally {
      clearTokenFromStorage()
    }
  },

  async getMe(token: string): Promise<{ id: string; email: string; name: string }> {
    return authFetch(`${API_URL}/api/auth/me`, token)
  },

  async changePassword(token: string, currentPassword: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    return authFetch(`${API_URL}/api/auth/change-password`, token, {
      method: 'PUT',
      body: JSON.stringify({ currentPassword, newPassword })
    })
  },

  // ========== ADMIN LOGS ==========
  
  async getAdminLogs(token: string, page: number = 1, limit: number = 50): Promise<{ logs: any[]; total: number; page: number; limit: number }> {
    return authFetch(`${API_URL}/api/admin/logs?page=${page}&limit=${limit}`, token)
  },

  // ========== DOCUMENTOS ==========
  
  async getDocuments(): Promise<DocumentData[]> {
    try {
      const response = await fetch(`${API_URL}/api/documents`)
      if (!response.ok) throw new Error('Erro ao buscar documentos')
      return response.json()
    } catch (error) {
      console.error('[DOCUMENTS] Erro:', error)
      return []
    }
  },

  async downloadDocument(documentId: string, fileName: string): Promise<void> {
    const response = await fetch(`${API_URL}/api/documents/${documentId}/download`)
    if (!response.ok) throw new Error('Erro ao baixar documento')
    
    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  },

  async getAdminDocuments(token: string): Promise<DocumentData[]> {
    return authFetch(`${API_URL}/api/admin/documents`, token)
  },

  async uploadDocument(token: string, data: UploadDocumentData): Promise<{ success: boolean; document: DocumentData }> {
    const formData = new FormData()
    formData.append('title', data.title)
    if (data.description) formData.append('description', data.description)
    if (data.category) formData.append('category', data.category)
    if (data.order !== undefined) formData.append('order', String(data.order))
    formData.append('file', data.file)

    try {
      const response = await fetch(`${API_URL}/api/admin/documents`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Erro ao fazer upload' }))
        throw new Error(error.error || 'Erro ao fazer upload')
      }

      return response.json()
    } catch (error) {
      console.error('[UPLOAD] Erro:', error)
      throw error
    }
  },

  async updateDocument(token: string, documentId: string, data: UpdateDocumentData): Promise<{ success: boolean; document: DocumentData }> {
    return authFetch(`${API_URL}/api/admin/documents/${documentId}`, token, {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  },

  async deleteDocument(token: string, documentId: string): Promise<{ success: boolean }> {
    return authFetch(`${API_URL}/api/admin/documents/${documentId}`, token, {
      method: 'DELETE'
    })
  },

  // ========== CONSULTA PÚBLICA ==========

  async submitConsulta(data: ConsultaSubmissionData): Promise<{ success: boolean; id: string }> {
    const response = await fetch(`${API_URL}/api/consulta`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro ao enviar contribuição' }))
      throw new Error(error.error || 'Erro ao enviar contribuição')
    }

    return response.json()
  },

  async getAdminConsultaSubmissions(token: string, page: number = 1, limit: number = 50): Promise<{ submissions: ConsultaSubmission[]; total: number; page: number; limit: number }> {
    return authFetch(`${API_URL}/api/admin/consulta?page=${page}&limit=${limit}`, token)
  },

  async exportConsultaCsv(token: string): Promise<void> {
    const response = await fetch(`${API_URL}/api/admin/consulta/export`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    if (!response.ok) throw new Error('Erro ao exportar contribuições')

    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'consulta-publica.csv'
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  },

  // ========== CONTEÚDO DO SITE ==========

  async getContent(): Promise<SiteContentMap> {
    try {
      const response = await fetch(`${API_URL}/api/content`)
      if (!response.ok) throw new Error('Erro ao buscar conteúdo')
      return await response.json()
    } catch (error) {
      console.error('[CONTENT] Erro:', error)
      return {}
    }
  },

  async updateContent(token: string, key: string, value: unknown): Promise<{ success: boolean }> {
    return authFetch(`${API_URL}/api/admin/content/${key}`, token, {
      method: 'PUT',
      body: JSON.stringify({ value })
    })
  }
}